/**
 * 模块: useSearchPerformanceUpload
 * 职责: 搜索查询绩效数据批量上传逻辑Hook
 * 限制: <280行
 */

import { useState, useRef } from 'react';
import { message } from 'antd';

interface UploadHookResult {
  uploading: boolean;
  uploadProgress: Record<string, string>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleBatchUpload: (files: File[], uploadFn: (file: File) => Promise<void>) => Promise<void>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>, uploadFn: (file: File) => Promise<void>) => void;
  triggerFileSelect: () => void;
}

export const useSearchPerformanceUpload = (): UploadHookResult => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * 批量上传CSV文件
   */
  const handleBatchUpload = async (files: File[], uploadFn: (file: File) => Promise<void>) => {
    if (files.length === 0) return;

    // 只接受CSV文件
    const csvFiles = files.filter((f) => f.name.toLowerCase().endsWith('.csv'));
    if (csvFiles.length === 0) {
      message.error('请上传CSV文件');
      return;
    }

    setUploading(true);
    setUploadProgress({});

    let successCount = 0;
    let failCount = 0;

    for (const file of csvFiles) {
      setUploadProgress((prev) => ({ ...prev, [file.name]: '⏳ 上传中...' }));

      try {
        await uploadFn(file);
        setUploadProgress((prev) => ({ ...prev, [file.name]: '✅ 成功' }));
        successCount++;
      } catch (error: any) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: `❌ ${error.message || '失败'}` }));
        failCount++;
      }

      // 短暂延迟，避免请求过快
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setUploading(false);

    // 显示结果
    if (successCount > 0 && failCount === 0) {
      message.success(`成功上传 ${successCount} 个文件`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`成功 ${successCount} 个，失败 ${failCount} 个`);
    } else {
      message.error('所有文件上传失败');
    }

    // 清空进度
    setTimeout(() => setUploadProgress({}), 3000);
  };

  /**
   * 处理文件选择
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, uploadFn: (file: File) => Promise<void>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    handleBatchUpload(fileArray, uploadFn);

    // 重置input
    event.target.value = '';
  };

  /**
   * 触发文件选择
   */
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return {
    uploading,
    uploadProgress,
    fileInputRef,
    handleBatchUpload,
    handleFileSelect,
    triggerFileSelect,
  };
};

